import Cookies from 'js-cookie';
import { type CookieConsentCookie, getCookie } from './CookieConsentUtils';
import MicroModal from 'micromodal';
import { ModalWrapper } from '3-components/Modal/ModalWrapper';

// interface CookieConsentSettings {
//     cookieName: string;
// }

interface CookieConsentProps {
    el: HTMLScriptElement;
    // props: CookieConsentSettings;
}

type ConsentValue = 'granted' | 'denied';

interface ConsentModeValue {
    ad_storage: ConsentValue;
    analytics_storage: ConsentValue;
    personalization_storage: ConsentValue;
    functionality_storage: ConsentValue;
    security_storage: ConsentValue;
}

declare global {
    interface Window {
        IFMSCR: {
            gtag: (arg: string, arg2: string, arg3: ConsentModeValue) => void;
            setAllConsent: (value: ConsentValue) => ConsentModeValue;
        };
        consentCookieName: string;
    }
}

const getAttributeValue = (source: HTMLElement, attrName: string): string => {
    if (attrName === 'style') {
        return source.getAttribute(attrName) || '';
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return (source as any)[attrName] || source.getAttribute(attrName);
};

const copyAttributes = (destination: HTMLElement, source: HTMLElement, excludes?: string[]): void => {
    const { attributes } = source;
    const len = attributes.length;
    for (let i = 0; i < len; i += 1) {
        const attrName = attributes[i].nodeName;

        if (!excludes?.length || !excludes.includes(attrName)) {
            destination.setAttribute(attrName, getAttributeValue(source, attrName));
        }
    }
};

const REJECT_BTN = '[data-reject]';
const ACCEPT_BTN = '[data-accept]';
const SCRIPT_SELECTOR = '[data-cc-script]';
const IMG_SELECTOR = '[data-cc-img]';
const IS_ACTIVE_CLASS = 'is-active';

// will be used for
const CookieConsent = ({ el }: CookieConsentProps): void => {
    // const { cookieName } = props;
    const cookieName = window.consentCookieName;

    const cookieData = getCookie(cookieName);

    // if user already accept or reject then just do nothing (arranged by BED)
    if (cookieData) {
        return;
    }

    const asModal = el.classList.contains('as-modal');

    if (asModal) {
        const { modalEl, modalContentEl } = ModalWrapper({
            id: 'cookie-consent-modal',
            disableCloseButton: true,
            disableCloseOnOverlayClick: true,
        });
        el.parentNode?.insertBefore(modalEl, el);
        modalContentEl.appendChild(el);
    }

    // other than that show the consent banner.
    el.classList.add(IS_ACTIVE_CLASS);

    const acceptBtn = el.querySelector(ACCEPT_BTN);
    const rejectBtn = el.querySelector(REJECT_BTN);

    // Copy attributes over to the new "revived" script
    const allScripts = document.querySelectorAll<HTMLScriptElement>(SCRIPT_SELECTOR);
    const allImages = document.querySelectorAll<HTMLImageElement>(IMG_SELECTOR);

    if (!acceptBtn || !rejectBtn) {
        return;
    }
    if (asModal) {
        MicroModal.show('cookie-consent-modal');
    }


    acceptBtn.addEventListener('click', () => {
        // if user accept cookies
        el.classList.remove(IS_ACTIVE_CLASS);
        const cookieValue: CookieConsentCookie = { acceptAll: true };
        Cookies.set(cookieName, JSON.stringify(cookieValue), { expires: 30 });

        if (asModal) {
            MicroModal.close('cookie-consent-modal');
        }

        if (allScripts) {
            allScripts.forEach(currentScript => {
                currentScript.removeAttribute('type');

                // create fresh script (with the same code)
                const freshScript = document.createElement('script');
                freshScript.textContent = currentScript.innerHTML;

                copyAttributes(freshScript, currentScript);
                currentScript.parentNode?.replaceChild(freshScript, currentScript);
            });
        }

        if (allImages) {
            allImages.forEach(currentImage => {
                const freshImage = document.createElement('img');
                const { src } = currentImage.dataset;

                if (src) {
                    copyAttributes(freshImage, currentImage, ['data-src']);
                    freshImage.setAttribute('src', src);
                    currentImage.parentNode?.replaceChild(freshImage, currentImage);
                }
            });
        }

        // Google Analytics Consent Mode
        // window.IFMSCR code: src/3-components/HeadScripts/CookieConsentHeadScript.js
        if (window.IFMSCR) {
            const consentValue = window.IFMSCR.setAllConsent('granted');
            window.IFMSCR.gtag('consent', 'update', consentValue);
        }
    });

    rejectBtn.addEventListener('click', () => {
        el.classList.remove(IS_ACTIVE_CLASS);
        const cookieValue: CookieConsentCookie = { acceptAll: false };
        Cookies.set(cookieName, JSON.stringify(cookieValue), { expires: 30 });

        if (asModal) {
            MicroModal.close('cookie-consent-modal');
        }

        // Google Analytics Consent Mode
        // window.IFMSCR code: src/3-components/HeadScripts/CookieConsentHeadScript.js
        if (window.IFMSCR) {
            const consentValue = window.IFMSCR.setAllConsent('denied');
            window.IFMSCR.gtag('consent', 'update', consentValue);
            // localStorage.setItem('consentMode', JSON.stringify(consentValue));
        }
    });
};

export default CookieConsent;
